/**
 * Plan 19, Task 4.2 — Neon-District backup-stub map assembly module.
 *
 * Reads board.json + theme.json + decorations.json from maps/neon-district/
 * and returns a minimal MapHandle with empty decorations (no GLB loading needed).
 *
 * @throws {MapAssemblyError} reason='invalid_board_json' if board.json fails shared validator.
 * @throws {MapAssemblyError} reason='invalid_theme_json' if theme.json fails Zod validation.
 * @throws {MapAssemblyError} reason='invalid_decorations_json' if decorations.json fails Zod.
 */

import * as THREE from 'three';
import neonDistrictBoardJson from '../../../../maps/neon-district/board.json';
import neonDistrictThemeJson from '../../../../maps/neon-district/theme.json';
import { loadMap as loadBoardSchema } from '../../../shared/board/map-loader';
import type { MapSchema } from '../../../shared/board/map-schema';
import type { MapHandle, ThemeOverride } from './types';
import { MapAssemblyError } from './types';
import type { SkyforgeSchema } from './schema';
import { SkyforgeSchemaSchema, ThemeJSONSchema } from './schema';
import type { LoadMapDeps } from './index';
import {
  getLightDirectionFromSunDirection,
  getSunDirectionFromSkyConfig,
} from '../atmosphere/daylight-sky';

/**
 * Assemble and return a Neon-District MapHandle.
 *
 * This is a backup-stub map — no GLB decorations, empty lanterns/crystals/fountainAnchors.
 * The board is a minimal 4-node looping graph that satisfies BoardGraph.validate().
 * The theme applies the cyan/magenta cyberpunk palette from maps/neon-district/theme.json.
 *
 * The async signature matches assembleSkyforge for API consistency.
 * The function is internally synchronous (no awaits) but returns a Promise.
 */
export async function assembleNeonDistrict(_deps: LoadMapDeps): Promise<MapHandle> {
  // ── Validate board.json ───────────────────────────────────────────────────
  const boardResult = loadBoardSchema(neonDistrictBoardJson as unknown as MapSchema);
  if (!boardResult.ok) {
    throw new MapAssemblyError(
      'invalid_board_json',
      `Neon-District board.json validation failed: ${boardResult.errors.join(', ')}`,
    );
  }

  // ── Build the SkyforgeSchema from board.json (bridges: [] — no bridges in neon-district) ──
  const boardJson = neonDistrictBoardJson as {
    name: string;
    version: number;
    startingTreasureChestNodeId?: string;
    startingWeaponsCacheNodeId?: string | null;
    spawnNodeIds: string[];
    nodes: unknown[];
  };
  const nodesParse = SkyforgeSchemaSchema.shape.nodes.safeParse(boardJson.nodes);
  if (!nodesParse.success) {
    throw new MapAssemblyError(
      'invalid_board_json',
      `Neon-District nodes validation failed: ${nodesParse.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(', ')}`,
    );
  }

  const schema: SkyforgeSchema = {
    name: boardJson.name,
    version: boardJson.version,
    bridges: [],
    nodes: nodesParse.data,
    startingTreasureChestNodeId: boardJson.startingTreasureChestNodeId,
    startingWeaponsCacheNodeId: boardJson.startingWeaponsCacheNodeId ?? null,
    spawnNodeIds: boardJson.spawnNodeIds,
  };

  // ── Validate theme.json ───────────────────────────────────────────────────
  const themeParse = ThemeJSONSchema.safeParse(neonDistrictThemeJson);
  if (!themeParse.success) {
    throw new MapAssemblyError(
      'invalid_theme_json',
      `Neon-District theme.json validation failed: ${themeParse.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(', ')}`,
    );
  }

  // ── Build ThemeOverride (per-field shallow merge, same pattern as assembleSkyforge) ──
  const baseSunDirection = getSunDirectionFromSkyConfig(themeParse.data.sky);
  const baseTheme: ThemeOverride = {
    skyTopColor: themeParse.data.skyTopColor,
    skyBottomColor: themeParse.data.skyBottomColor,
    sky: themeParse.data.sky,
    clouds: themeParse.data.clouds,
    fog: { color: themeParse.data.fogColor, density: themeParse.data.fogDensity },
    lightingRig: {
      ...themeParse.data.lightingRig,
      keyDirection: getLightDirectionFromSunDirection(baseSunDirection),
    },
  };

  const themeOverride = _deps.themeOverride;
  const sky = {
    ...baseTheme.sky,
    ...(themeOverride?.sky ?? {}),
  };
  const clouds = {
    ...baseTheme.clouds,
    ...(themeOverride?.clouds ?? {}),
  };
  const sunDirection = getSunDirectionFromSkyConfig(sky);
  const lightingRig = {
    ...baseTheme.lightingRig,
    ...(themeOverride?.lightingRig ?? {}),
    shadow: {
      ...(baseTheme.lightingRig.shadow ?? {}),
      ...(themeOverride?.lightingRig?.shadow ?? {}),
    },
    keyDirection:
      themeOverride?.lightingRig?.keyDirection ?? getLightDirectionFromSunDirection(sunDirection),
  };

  const theme: ThemeOverride = {
    skyTopColor: themeOverride?.skyTopColor ?? baseTheme.skyTopColor,
    skyBottomColor: themeOverride?.skyBottomColor ?? baseTheme.skyBottomColor,
    sky,
    clouds,
    fog: {
      ...baseTheme.fog,
      ...(themeOverride?.fog ?? {}),
    },
    lightingRig,
  };

  // ── State ─────────────────────────────────────────────────────────────────
  const skyquakeListeners = new Set<(durationSeconds: number) => void>();
  let disposed = false;

  // ── MapHandle ─────────────────────────────────────────────────────────────
  return {
    name: 'neon-district',

    schema,

    theme,

    fx: {
      toneMapping: 'NoTone',
      toneMappingExposure: 1,
      bloomStrength: 0.6,
      bloomRadius: 0.85,
      bloomThreshold: 0.85,
    },

    camera: null,

    getLanterns(): readonly THREE.Object3D[] {
      return Object.freeze([]);
    },

    getCrystals(): readonly THREE.Object3D[] {
      return Object.freeze([]);
    },

    getFountainAnchors(): readonly THREE.Vector3[] {
      return Object.freeze([]);
    },

    onSkyQuake(cb: (durationSeconds: number) => void): () => void {
      skyquakeListeners.add(cb);
      return () => {
        skyquakeListeners.delete(cb);
      };
    },

    /** Idempotent — no resources to dispose (no GLB loads, no InstancedMeshes). */
    dispose(): void {
      if (disposed) return;
      disposed = true;
      skyquakeListeners.clear();
    },
  };
}
